import { Idea, ParticipationStatus, w2d2dApiClient } from "../../../w2d2d-api-client";
import { AvatarGroup, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { AddCircleOutlineOutlined, Favorite, RemoveCircleOutline } from "@mui/icons-material";
import { User } from "../../../../appfw/api-client/AppFwApiClient";
import { useIdeasControl } from "../../hooks/useIdeasControl";
import AvatarWidget from "../users/AvatarWidget";
import { ResourceWrapper } from "../../../../eventer-pod/client/ResourceWrapper.ts";
import DialogActionButton from "../../../../appfw/ui/dialogs/DialogActionButton.tsx";
import AppFwBottomSheet from "../../../../appfw/ui/components/bottom-sheet.tsx";
import { useUser } from "../../../../appfw/users/User.tsx";
import { Box, Typography } from "@mui/material";

interface ParticipationWidgetProps {
    readonly user: User
    readonly event: ResourceWrapper
    readonly idea: Idea | null
}

function ParticipantRow({ id }: { id: string }) {
    const { data: user } = useUser(id);
    if (!user) return null;
    return (
        <Stack direction="row" spacing={2} alignItems="center">
            <AvatarWidget id={id} />
            <Typography>{user.name}</Typography>
        </Stack>
    )
}

export default function ParticipationWidget(props: ParticipationWidgetProps) {
    const { updateIdea } = useIdeasControl()
    const { user, event, idea } = props
    const [isOpen, setIsOpen] = useState(false);

    const eventUri = event?.subject

    const handleParticipation = useCallback(async (
        status: ParticipationStatus,
        currentStatus: boolean
    ) => {
        if (!eventUri) return

        let updatedIdea: Idea

        if (idea) {
            // Idea exists - update participation
            if (currentStatus) {
                updatedIdea = await w2d2dApiClient.deleteIdeaParticipation({ id: idea.id })
            } else {
                switch (status) {
                    case "accept":
                        updatedIdea = await w2d2dApiClient.acceptIdea({ id: idea.id })
                        break
                    case "interested":
                        updatedIdea = await w2d2dApiClient.interestedInIdea({ id: idea.id })
                        break
                    case "reject":
                        updatedIdea = await w2d2dApiClient.rejectIdea({ id: idea.id })
                        break
                }
            }
            updateIdea(updatedIdea)
        } else {
            // No idea exists - create new idea with participation status
            updatedIdea = await w2d2dApiClient.newIdeaByEvent({
                eventUri: eventUri,
                participationStatus: status
            })
            updateIdea(updatedIdea)
        }
    }, [idea, eventUri, updateIdea])

    // Determine current states
    const isAccepted = idea?.accepted?.includes(user.id) || false
    const isInterested = idea?.interested?.includes(user.id) || false
    const isRejected = idea?.rejected?.includes(user.id) || false

    const handleHeaderClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        // Prevent toggle if clicking on a button or an interactive element within the header
        // DialogActionButton usually renders a clickable element. We check for common button traits.
        if (target.closest('button') || target.closest('[role="button"]') || target.closest('.MuiButtonBase-root')) {
            return;
        }
        setIsOpen(!isOpen);
    };

    return (
        <AppFwBottomSheet
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <Stack spacing={0}>
                {/* Buttons Header (Fixed part essentially) */}
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        width: '100%',
                        pb: isOpen ? 2 : 0,
                        transition: 'padding 0.3s',
                        cursor: 'pointer' // Indicate clickable area
                    }}
                    onClick={handleHeaderClick}
                >
                    {/* Accept Column */}
                    <Stack spacing={1} alignItems="center" justifyContent="flex-start" sx={{ flex: 1 }}>
                        <DialogActionButton
                            outlet={
                                <AddCircleOutlineOutlined sx={{
                                    color: isAccepted ? 'primary.main' : 'action.active'
                                }} />
                            }
                            dialogContent={
                                isAccepted
                                    ? "Möchtest du deine Zusage für dieses Event wirklich zurückziehen?"
                                    : "Möchtest du für dieses Event zusagen?"
                            }
                            dialogActions={[
                                { label: 'Abbrechen' },
                                {
                                    label: isAccepted ? 'Zurückziehen' : 'Zusagen',
                                    action: async () => await handleParticipation("accept", isAccepted)
                                }
                            ]}
                        />
                        <Box sx={{
                            height: isOpen ? 0 : 'auto',
                            opacity: isOpen ? 0 : 1,
                            overflow: 'hidden',
                            transition: 'all 0.3s ease-in-out'
                        }}>
                            <AvatarGroup max={5} total={idea?.accepted?.length || 0} sx={{ marginLeft: 0 }}>
                                {idea?.accepted?.map((userId) => (
                                    <AvatarWidget key={userId} id={userId} size={32} />
                                ))}
                            </AvatarGroup>
                        </Box>
                    </Stack>

                    {/* Interested Column */}
                    <Stack spacing={1} alignItems="center" justifyContent="flex-start" sx={{ flex: 1 }}>
                        <DialogActionButton
                            outlet={
                                <Favorite sx={{
                                    color: isInterested ? 'primary.main' : 'action.active'
                                }} />
                            }
                            dialogContent={
                                isInterested
                                    ? "Möchtest du dein Interesse an diesem Event zurückziehen?"
                                    : "Bist du an diesem Event interessiert?"
                            }
                            dialogActions={[
                                { label: 'Abbrechen' },
                                {
                                    label: isInterested ? 'Entfernen' : 'Interessiert',
                                    action: async () => await handleParticipation("interested", isInterested)
                                }
                            ]}
                        />
                        <Box sx={{
                            height: isOpen ? 0 : 'auto',
                            opacity: isOpen ? 0 : 1,
                            overflow: 'hidden',
                            transition: 'all 0.3s ease-in-out'
                        }}>
                            <AvatarGroup max={5} total={idea?.interested?.length || 0} sx={{ marginLeft: 0 }}>
                                {idea?.interested?.map((userId) => (
                                    <AvatarWidget key={userId} id={userId} size={32} />
                                ))}
                            </AvatarGroup>
                        </Box>
                    </Stack>

                    {/* Reject Column */}
                    <Stack spacing={1} alignItems="center" justifyContent="flex-start" sx={{ flex: 1 }}>
                        <DialogActionButton
                            outlet={
                                <RemoveCircleOutline sx={{
                                    color: isRejected ? 'primary.main' : 'action.active'
                                }} />
                            }
                            dialogContent={
                                isRejected
                                    ? "Möchtest du deine Absage zurückziehen?"
                                    : "Möchtest du für dieses Event absagen?"
                            }
                            dialogActions={[
                                { label: 'Abbrechen' },
                                {
                                    label: isRejected ? 'Zurückziehen' : 'Absagen',
                                    action: async () => await handleParticipation("reject", isRejected)
                                }
                            ]}
                        />
                        <Box sx={{
                            height: isOpen ? 0 : 'auto',
                            opacity: isOpen ? 0 : 1,
                            overflow: 'hidden',
                            transition: 'all 0.3s ease-in-out'
                        }}>
                            <AvatarGroup max={5} total={idea?.rejected?.length || 0} sx={{ marginLeft: 0 }}>
                                {idea?.rejected?.map((userId) => (
                                    <AvatarWidget key={userId} id={userId} size={32} />
                                ))}
                            </AvatarGroup>
                        </Box>
                    </Stack>
                </Stack>

                {/* Details List (Only visible when open usually, but we keep it here so it flows) */}
                {isOpen && (
                    <Box sx={{ animation: 'fadeIn 0.5s' }}>
                        <style>{`
                            @keyframes fadeIn {
                                from { opacity: 0; }
                                to { opacity: 1; }
                            }
                        `}</style>
                        <Stack spacing={3}>
                            {idea?.accepted && idea.accepted.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Zusagen ({idea.accepted.length})
                                    </Typography>
                                    <Stack spacing={1}>
                                        {idea.accepted.map(id => <ParticipantRow key={id} id={id} />)}
                                    </Stack>
                                </Box>
                            )}

                            {idea?.interested && idea.interested.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Interessiert ({idea.interested.length})
                                    </Typography>
                                    <Stack spacing={1}>
                                        {idea.interested.map(id => <ParticipantRow key={id} id={id} />)}
                                    </Stack>
                                </Box>
                            )}

                            {idea?.rejected && idea.rejected.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Abgesagt ({idea.rejected.length})
                                    </Typography>
                                    <Stack spacing={1}>
                                        {idea.rejected.map(id => <ParticipantRow key={id} id={id} />)}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </AppFwBottomSheet>
    )
}
